import * as PIXI from 'pixi.js';

import fontkit from 'fontkit-next';
import type { Font } from 'fontkit';
import { FONT_STATUS } from '../../../constants';
import { createValidator, retry } from '../../../libs/retry';
import { assertResponseOk } from '../../../validators';
import { generateCssFamilyName } from '../fontkitUtils';
import { isBreakingSpace, isNewline } from '../textMetricsHelpers';
import type { FontManager } from '../FontManager';


type FontLoaderSettings = {
    parent: FontManager;
    sourceUrl: string;
};

/**
 * The FontLoader class is created by the FontManager and loads a given font. It will always load the fontkit font
 * as well as the load the css font faces. The underlying loading status is always tied to just the fontkit font.
 */
export class FontLoader extends PIXI.utils.EventEmitter {
    parent = undefined;
    classType = 'Font';
    font: Font = null;
    status: FONT_STATUS = FONT_STATUS.ADDED;
    sourceUrl: string;
    #familyName: string;

    constructor(settings: FontLoaderSettings) {
        super();
        this.parent = settings.parent;
        this.sourceUrl = settings.sourceUrl;
    }

    #knownUnsupportedCodePoints = new Set();
    #knownSupportedCodePoints = new Set();

    /**
     * Returns true if the fontkit font can render the given string
     */
    canRenderText(originalText: string): { supported: boolean; missing: string; } {
        if (!this.isLoaded) {
            return { supported: false, missing: originalText };
        }

        const missing: number[] = [];
        const codePointsToAnalyze: number[] = [];
        for (const character of originalText) {
            const codePoint = character.codePointAt(0);

            if (isNewline(character)) {
                continue;
            }
            else if (isBreakingSpace(character)) {
                continue;
            }
            else if (this.#knownSupportedCodePoints.has(codePoint)) {
                continue;
            }
            else if (this.#knownUnsupportedCodePoints.has(codePoint)) {
                missing.push(codePoint);
            }
            else {
                codePointsToAnalyze.push(codePoint);
            }
        }

        if (codePointsToAnalyze.length === 0) {
            const supported = missing.length === 0;
            return { supported, missing: String.fromCodePoint(...missing) };
        }

        codePointsToAnalyze.forEach(codePoint => {
            if (this.font.hasGlyphForCodePoint(codePoint)) {
                this.#knownSupportedCodePoints.add(codePoint);
            }
            else {
                this.#knownUnsupportedCodePoints.add(codePoint);
                missing.push(codePoint);
            }
        });

        const supported = missing.length === 0;

        return { supported, missing: String.fromCodePoint(...missing) };
    }

    /**
     * Loads the specified sourceUrl. Once the fontkit is loaded, a 'loaded' event will be emitted on both the font
     * and parent level.
     */
    async load(): Promise<void> {
        const validator = createValidator(assertResponseOk);
        const response = await retry(5, validator(fetch), this.sourceUrl);
        const buffer = Buffer.from(await response.arrayBuffer());
        const font = fontkit.create(buffer);
        this.font = font;
        this.status = FONT_STATUS.LOADED;

        const fontFace = new FontFace(this.familyName, buffer);
        document.fonts.add(fontFace);
    }

    get isLoaded() {
        return this.status === FONT_STATUS.LOADED;
    }

    get failedToLoad() {
        return this.status === FONT_STATUS.FAILED;
    }

    /**
     * Return font familyName if it is defined in the font, otherwise returns
     * a md5 hash based on the source URL.
     */
    get familyName(): string {
        if (this.#familyName) {
            return this.#familyName;
        }
        if (!this.isLoaded) {
            throw new Error('familyName called before font was loaded.');
        }
        if (this.font == null) {
            throw new Error('Font should have been loaded but it is null.');
        }
        this.#familyName = generateCssFamilyName(this.sourceUrl);
        return this.#familyName;
    }
}
